import { compose, lifecycle, withHandlers, withProps, withState } from 'recompose';
import { connect } from 'react-redux';
import { reduxForm, formValueSelector } from 'redux-form';
import Joi from 'joi-full';
import { withRouter } from 'react-router';
import { prop } from 'ramda';

import withLoading from '../../../../common/withLoading';
import withNotifications from '../../../../common/withNotifications';
import confirmModal from '../../../../common/confirmModal';
import { syncValidateData } from '../../../../common/validator';
import { getUserUrlPrefix, getActiveBrandId, getShopById } from '../../../../auth/selectors';
import { getAddresses, getPoLocation } from '../store/selectors';
import { fetchLocations, updatePoLocation } from '../store/actions';
import getInitialValues from './getInitialValues';
import { canUserModifyProducts } from '../../../../common/helpers/users';

const LOCATION_FORM = 'LOCATION_FORM';

const schema = Joi.object().keys({
  name: Joi.string().required(),
  prefix: Joi.string().allow('').allow(null).optional(),
  startingPONumber: Joi.number().empty('').allow(null).optional(),
  billingAddressId: Joi.string(),
});

const validate = values => syncValidateData(values, schema);

export const formConfig = {
  form: LOCATION_FORM,
  validate,
};

export const formSelector = formValueSelector(LOCATION_FORM);

const form = reduxForm({
  form: LOCATION_FORM,
  validate,
});

const EditLocationContainer = compose(
  withRouter,
  withNotifications,
  connect((state, ownProps) => {
    const location = getPoLocation(state, ownProps.params.id);
    return {
      addressList: getAddresses(state),
      canEditProducts: canUserModifyProducts(state.auth.permissions),
      location,
      showLoading: !location,
      baseUrl: `${getUserUrlPrefix(state)}/products/purchase-orders/locations`,
      brandId: getActiveBrandId(state),
      shopName: prop('name')(getShopById(state, ownProps.params.id)),
    };
  }, {
    fetchLocations,
    updatePoLocation,
  }),
  lifecycle({
    componentDidMount() {
      if (!this.props.location) {
        this.props.fetchLocations(this.props.brandId);
      }
    },
  }),
  withLoading,
  withState('busy', 'setBusy', false),
  withProps(props => ({
    busy: false,
    initialValues: getInitialValues(props.location, props.shopName),
  })),
  withHandlers({
    onSubmit: props => async (values) => {
      const confirmed = await confirmModal({
        type: 'defaultSafe',
        title: 'Edit location warning',
        message: `Are you sure you want to edit this location?\n
        Please note that editing the PO Prefix, PO starting number, or Bill To address will take effect with the next generated POs for this location. This change won't retroactively affect past POs.`,
        confirmButtonText: 'Save',
        negativeButtonText: 'Cancel',
      });

      if (!confirmed) {
        return false;
      }

      props.setBusy(true);
      try {
        await props.updatePoLocation(
          props.location.ownerId,
          {
            prefix: values.prefix || '',
            startingPONumber: parseInt(values.startingPONumber, 10) || 0,
            billingAddressId: values.billingAddressId,
          }
        );
        props.setBusy(false);
        props.router.push(props.baseUrl);
        props.addNotification({
          title: 'Success',
          level: 'success',
          message: 'Location details updated.',
        });
      } catch (e) {
        props.setBusy(false);
      }
      return true;
    },
  }),
  form,
);

export default EditLocationContainer;
